import React, {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {useDispatch} from "react-redux";
import {useQueryClient} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import axios from "axios";

import {Layout} from "components/Layout";
import {BackButton} from "components/BackButton";
import {showAlert} from "redux/actions/setAppActions";

export const CategoriesPage = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const pupilId = useParams().id;
    const [allTargetGroupsLvl1, setAllTargetGroupsLvl1] = useState([]);
    const [selectedLvl2Groups, setSelectedLvl2Groups] = useState([]);
    const [isCreatingGroup, setIsCreatingGroup] = useState(false);
    const [newGroupName, setNewGroupName] = useState("");
    const [selectedParentGroupId, setSelectedParentGroupId] = useState(null);
    const [isCreatingInProgress, setIsCreatingInProgress] = useState(false);

    const loadTargetGroups = async () => {
        const { data } = await axios.get("targetGroups/level1/with-children", {
            params: { pupilId },
        });

        setAllTargetGroupsLvl1(data.targetGroups);
    };

    useEffect(() => {
        loadTargetGroups();
    }, [pupilId]);

    useEffect(() => {
        (async () => {
            const { data } = await axios.get(`categories/pupil/${pupilId}`);
            const pupilLvl2Ids = [];

            (data.targetGroupsLvl1 || []).forEach((lvl1Group) => {
                (lvl1Group.children || []).forEach((lvl2Group) => {
                    pupilLvl2Ids.push(lvl2Group._id);
                });
            });

            setSelectedLvl2Groups(pupilLvl2Ids);
        })();
    }, [pupilId]);

    const handleLvl2Checkbox = (lvl2GroupId) => {
        setSelectedLvl2Groups((prev) => (
            prev.includes(lvl2GroupId)
                ? prev.filter((id) => id !== lvl2GroupId)
                : [...prev, lvl2GroupId]
        ));
    };

    const handleCreateGroupClick = (parentGroupId) => {
        setSelectedParentGroupId(parentGroupId);
        setIsCreatingGroup(true);
        setNewGroupName("");
    };

    const handleCancelCreateGroup = () => {
        setIsCreatingGroup(false);
        setNewGroupName("");
        setSelectedParentGroupId(null);
    };

    const handleCreateGroup = async () => {
        if (!newGroupName.trim()) {
            dispatch(showAlert({ type: "error", text: "Введите название группы" }));
            return;
        }

        if (isCreatingInProgress) {
            return;
        }

        setIsCreatingInProgress(true);

        try {
            await axios.post("/targetGroups/create/2", {
                name: newGroupName,
                parentGroup: selectedParentGroupId,
                pupilId,
            });

            await loadTargetGroups();
            dispatch(showAlert({ type: "success", text: "Группа успешно создана" }));
            handleCancelCreateGroup();
        } catch (error) {
            console.error("Error creating group:", error);
            dispatch(showAlert({
                type: "error",
                text: error.response?.data?.message || "Ошибка при создании группы",
            }));
        } finally {
            setIsCreatingInProgress(false);
        }
    };

    const submitHandler = async (event) => {
        event.preventDefault();

        try {
            await axios.put(`/categories/pupil/${pupilId}/target-groups`, {
                targetGroupsLvl2: selectedLvl2Groups,
            });

            await queryClient.invalidateQueries({ queryKey: ["pupils/with-categories"] });
            await queryClient.invalidateQueries({ queryKey: ["targetGroups/level1"] });
            await queryClient.invalidateQueries({ queryKey: ["targetGroups/level2"] });
            await loadTargetGroups();

            dispatch(showAlert({ type: "success", text: "Целевые группы успешно обновлены" }));
        } catch (error) {
            console.error("Error updating target groups:", error);
            dispatch(showAlert({
                type: "error",
                text: error.response?.data?.message || "Ошибка при обновлении целевых групп",
            }));
        }
    };

    return (
        <Layout>
            <div className="page">
                <header className="page__header">
                    <BackButton />
                </header>
                <div className="page__content">
                    <h3>{t("risk_groups")}</h3>

                    {allTargetGroupsLvl1.length > 0 ? (
                        <div style={{ marginBottom: "2rem" }}>
                            <div className="list">
                                {allTargetGroupsLvl1.map((lvl1Group) => (
                                    <div key={lvl1Group._id}>
                                        <div
                                            style={{
                                                fontSize: "22px",
                                                padding: "0.75rem 0",
                                                fontWeight: "500",
                                                borderRadius: "8px",
                                                marginBottom: "0.5rem",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                            }}
                                        >
                                            <p style={{ margin: 0 }}>{lvl1Group.name}</p>
                                            <button
                                                onClick={() => handleCreateGroupClick(lvl1Group._id)}
                                                style={{
                                                    background: "none",
                                                    border: "none",
                                                    fontSize: "24px",
                                                    cursor: "pointer",
                                                    color: "#4a90e2",
                                                    padding: "0 0.25rem 0 0.5rem",
                                                    lineHeight: "1",
                                                }}
                                                title="Создать подгруппу"
                                            >
                                                +
                                            </button>
                                        </div>

                                        <div
                                            style={{
                                                marginLeft: "1.5rem",
                                                marginBottom: "1rem",
                                                borderLeft: "3px solid #4a90e2",
                                                paddingLeft: "0.5rem",
                                            }}
                                        >
                                            {lvl1Group.children?.length > 0 && lvl1Group.children.map((lvl2Group) => (
                                                <div key={lvl2Group._id} className="list__checkbox">
                                                    <input
                                                        type="checkbox"
                                                        id={lvl2Group._id}
                                                        name={lvl2Group._id}
                                                        value={lvl2Group._id}
                                                        onChange={() => handleLvl2Checkbox(lvl2Group._id)}
                                                        checked={selectedLvl2Groups.includes(lvl2Group._id)}
                                                    />
                                                    <label htmlFor={lvl2Group._id}>
                                                        <div>
                                                            <p>{lvl2Group.name}</p>
                                                        </div>
                                                    </label>
                                                </div>
                                            ))}

                                            {isCreatingGroup && selectedParentGroupId === lvl1Group._id && (
                                                <div
                                                    style={{
                                                        padding: "0.5rem",
                                                        backgroundColor: "#f9f9f9",
                                                        borderRadius: "4px",
                                                        marginTop: "0.5rem",
                                                    }}
                                                >
                                                    <input
                                                        type="text"
                                                        value={newGroupName}
                                                        onChange={(event) => setNewGroupName(event.target.value)}
                                                        placeholder="Название группы"
                                                        style={{
                                                            width: "100%",
                                                            padding: "0.5rem",
                                                            border: "1px solid #ddd",
                                                            borderRadius: "4px",
                                                            marginBottom: "0.5rem",
                                                        }}
                                                        autoFocus
                                                    />
                                                    <div style={{ display: "flex", gap: "0.5rem" }}>
                                                        <button
                                                            onClick={handleCreateGroup}
                                                            className="button button--primary"
                                                            style={{ fontSize: "0.875rem", padding: "0.25rem 0.75rem" }}
                                                            disabled={isCreatingInProgress}
                                                        >
                                                            {isCreatingInProgress ? "Создание..." : "Создать"}
                                                        </button>
                                                        <button
                                                            onClick={handleCancelCreateGroup}
                                                            className="button button--secondary"
                                                            style={{ fontSize: "0.875rem", padding: "0.25rem 0.75rem" }}
                                                            disabled={isCreatingInProgress}
                                                        >
                                                            Отмена
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="send-btn send-btn-static">
                                <button onClick={submitHandler} className="main-btn">
                                    {t("save")}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <p style={{ textAlign: "center", padding: "2rem", color: "#888" }}>
                            Загрузка целевых групп...
                        </p>
                    )}
                </div>
            </div>
        </Layout>
    );
};
